import type {
  BuyerAssignmentQueueParams,
  BuyerLeadListParams,
  BuyerRecordKind,
  BuyerSubmissionListParams,
  ListingAgentListParams,
  ListingListParams,
} from "@/api/buyers";
import type { BuyerResearchCandidateListQuery } from "@/api/buyerResearch";

/**
 * Query keys for the Buyer CRM.
 *
 * Every read registers here and every mutation invalidates by PREFIX. The
 * QueryClient runs at `staleTime: 5min` with `refetchOnWindowFocus: false`, so
 * a missed invalidation is stale for minutes with no recovery — which is why
 * `staleAfterSaveGate.test.ts` requires `queryKey` literals to live in a
 * factory file like this one rather than inline at the call site.
 */
export const buyerKeys = {
  all: ["buyers"] as const,

  lists: () => [...buyerKeys.all, "list"] as const,
  list: (params: BuyerLeadListParams) => [...buyerKeys.lists(), params] as const,

  /** Separate from the list so a page can refetch counts without refetching
   *  rows, but under the same `all` prefix so one invalidation covers both. */
  stats: (params: BuyerLeadListParams) => [...buyerKeys.all, "stats", params] as const,

  details: () => [...buyerKeys.all, "detail"] as const,
  detail: (id: string) => [...buyerKeys.details(), id] as const,

  /** The education email's preview state — the resolved copy, the literal
   *  recipient and whether it can send. Under the `all` prefix so a send is
   *  covered by the same invalidation that refreshes the buyer's detail (the
   *  stamp the Qualification card and the Overview milestone both read lives on
   *  the buyer), and separate from `detail` so opening the preview does not
   *  refetch the whole record. */
  educationEmail: (id: string) =>
    [...buyerKeys.all, "education-email", id] as const,

  /** The one-call screen's state (the call's prequalification answers, the
   *  consent script, whether Prequalify is available, the newest call prequal).
   *  Under `all` so any buyer mutation covers it. */
  prequalCall: (id: string) =>
    [...buyerKeys.all, "prequal-call", id] as const,

  /** One call prequalification's review screen. Under `prequalCall` so a send
   *  or a withhold that invalidates the call state also refreshes the review. */
  prequalCallReview: (id: string, submissionId: string) =>
    [...buyerKeys.all, "prequal-call", id, "review", submissionId] as const,

  /** The linked prequalification (PR 2c). Under the `all` prefix so a buyer
   *  mutation covers it, and separate from `detail` so linking does not force a
   *  refetch of the whole record. */
  prequalification: (id: string) =>
    [...buyerKeys.all, "prequalification", id] as const,

  /** The buyer's "Referred by" (D4). Under the `all` prefix so every buyer
   *  mutation refreshes it; package states read it too, so its writer also
   *  invalidates `buyerSubmissionKeys.all`. */
  referredBy: (id: string) => [...buyerKeys.all, "referred-by", id] as const,
  prequalificationCandidates: (id: string, search: string) =>
    [...buyerKeys.all, "prequalification-candidates", id, search] as const,

  /** Invitations sent to one buyer (PR 3b). Under the `all` prefix so sending
   *  or resending is covered by the same invalidation as a link change. */
  prequalificationInvites: (id: string) =>
    [...buyerKeys.all, "prequalification-invites", id] as const,

  /** Every prequalification this buyer completed (PR 3b) — the attempts list
   *  and the letter-override picker read the same key. */
  prequalificationAttempts: (id: string) =>
    [...buyerKeys.all, "prequalification-attempts", id] as const,

  /**
   * The qualification interview.
   *
   * <p>All four keys sit under the `all` prefix, so ONE invalidation after an
   * autosave, a completion or an override refreshes the drawer, the version
   * list and the buyer's own detail together — completion projects onto
   * `BuyerLeadQualification` and can stamp `qualifiedAtUtc`, so the detail read
   * genuinely moves and a narrower blast radius would leave the Qualification
   * tab claiming the old assessment for the five minutes of `staleTime`, with
   * no recovery.</p>
   *
   * <p>The registry is buyer-AGNOSTIC and deliberately keyed outside
   * `buyerKeys.all`: it is the served question set, identical for every buyer,
   * so caching it per buyer would refetch the whole question set on every
   * record and evict it on every unrelated buyer write. Its wording is
   * admin-editable (the interview wording editor), so the key carries the
   * wording version: a numbered version is immutable once published and caches
   * forever; `"current"` is whatever is in force now. `interviewRegistryAll`
   * is the prefix a publish invalidates.</p>
   */
  interviewRegistryAll: () => ["buyer-interview-registry"] as const,
  interviewRegistry: (wordingVersion?: number) =>
    ["buyer-interview-registry", wordingVersion ?? "current"] as const,
  interview: (id: string) => [...buyerKeys.all, "interview", id] as const,
  interviewVersions: (id: string) =>
    [...buyerKeys.all, "interview-versions", id] as const,
  interviewVersion: (id: string, version: number) =>
    [...buyerKeys.all, "interview-version", id, version] as const,

  /** Suspected duplicates for a typed email/phone, as a create form types.
   *  Under the `all` prefix so creating a buyer re-evaluates it. */
  duplicateCheck: (email: string, phone: string, excludeId?: string) =>
    [...buyerKeys.all, "duplicate-check", email, phone, excludeId ?? ""] as const,

  /**
   * The buyer's associated listings (WS-3) — the shortlist.
   *
   * Under the `all` prefix so tying, editing or removing one is covered by the
   * same invalidation as any other buyer write. Every row's badge is derived
   * from its attached submission, so anything that moves a submission's status
   * must invalidate THIS key too, not just `buyerSubmissionKeys` — the badge is
   * served from here.
   */
  associatedListings: (id: string) =>
    [...buyerKeys.all, "associated-listings", id] as const,

  /**
   * Listing research (the Research tab) — the run list, one run with its
   * candidates, and the buyer's dismissal set.
   *
   * All under the `all` prefix, and all under ONE `research` prefix beneath
   * it: attaching a candidate creates an association (so the shortlist under
   * `associatedListings` moves) AND flips the candidate's attached state on the
   * run it belongs to, and dismissing moves both the run's counts and the
   * dismissal list. A mutation invalidating `all` reaches every one of them in
   * one call, and a narrower `research(id)` reaches the three research reads
   * without refetching the record.
   */
  research: (buyerId: string) => [...buyerKeys.all, "research", buyerId] as const,
  researchRuns: (buyerId: string) => [...buyerKeys.research(buyerId), "runs"] as const,
  researchRun: (buyerId: string, runId: string) =>
    [...buyerKeys.research(buyerId), "run", runId] as const,
  /**
   * The run's candidate LIST (R5), under the run's own key so anything that
   * invalidates a run reaches its list too. Every page and filter combination
   * shares this prefix, which is what the poll's transition invalidation and a
   * reveal target.
   */
  researchCandidates: (buyerId: string, runId: string) =>
    [...buyerKeys.researchRun(buyerId, runId), "candidates"] as const,
  researchCandidatePage: (buyerId: string, runId: string, query: BuyerResearchCandidateListQuery) =>
    [...buyerKeys.researchCandidates(buyerId, runId), "page", query] as const,
  /** One candidate's full record, fetched when its row is expanded. */
  researchCandidate: (buyerId: string, runId: string, candidateId: string) =>
    [...buyerKeys.researchCandidates(buyerId, runId), "detail", candidateId] as const,
  researchDismissals: (buyerId: string) =>
    [...buyerKeys.research(buyerId), "dismissals"] as const,

  /** The buyer's open combined intro email draft — one email introducing
   *  several of their listings. Under `all`, so a send (which moves the
   *  submissions' intro stamps and send history) refreshes it with them. */
  combinedIntroEmail: (buyerId: string) =>
    [...buyerKeys.all, "combined-intro-email", buyerId] as const,
};

export const listingKeys = {
  all: ["listings"] as const,
  lists: () => [...listingKeys.all, "list"] as const,
  list: (params: ListingListParams) => [...listingKeys.lists(), params] as const,

  /** Index-page tile counts. Separate from the list so the deck can refetch
   *  without the rows, but under the same `all` prefix so one invalidation
   *  covers both — the shape `buyerKeys.stats` already uses. */
  stats: (params: ListingListParams) => [...listingKeys.all, "stats", params] as const,
  details: () => [...listingKeys.all, "detail"] as const,
  detail: (id: string) => [...listingKeys.details(), id] as const,

  /** Agents are SHARED records, not children of one listing — but they live
   *  under the listing prefix so upserting one refreshes the listing that
   *  renders its name. */
  agents: (params: ListingAgentListParams) =>
    [...listingKeys.all, "agents", params] as const,
  agent: (id: string) => [...listingKeys.all, "agent", id] as const,

  /** The shared agent picker's typeahead. Under `all`, so creating or editing
   *  an agent anywhere refreshes an open picker's results. */
  agentSearch: (term: string) => [...listingKeys.all, "agent-search", term] as const,

  duplicateCheck: (rawUrl: string, businessName: string, excludeId?: string) =>
    [...listingKeys.all, "duplicate-check", rawUrl, businessName, excludeId ?? ""] as const,

  /**
   * The SELLER's prequalification, addressed by the listing that owns it.
   *
   * 🔴 Under the LISTING prefix, and the submission-addressed twins under
   * `buyerSubmissionKeys`, even though both read the same row. Every seller
   * mutation already invalidates BOTH prefixes for exactly this reason — the
   * write lands on the shared listing, so the submission-side summary and the
   * escalation warning have to learn about it too — so a key living under its
   * own address costs nothing and keeps each surface's reads findable from the
   * surface that owns them.
   */
  sellerPrequalification: (listingId: string) =>
    [...listingKeys.all, "seller-prequalification", listingId] as const,
  sellerPrequalificationCandidates: (listingId: string, search: string) =>
    [...listingKeys.all, "seller-prequalification-candidates", listingId, search] as const,
  sellerPrequalificationInvites: (listingId: string) =>
    [...listingKeys.all, "seller-prequalification-invites", listingId] as const,
};

export const buyerSubmissionKeys = {
  all: ["buyer-submissions"] as const,
  lists: () => [...buyerSubmissionKeys.all, "list"] as const,
  list: (params: BuyerSubmissionListParams) =>
    [...buyerSubmissionKeys.lists(), params] as const,
  details: () => [...buyerSubmissionKeys.all, "detail"] as const,
  detail: (id: string) => [...buyerSubmissionKeys.details(), id] as const,

  /** Separate from the list so the stat bar can refetch without the rows, but
   *  under the same `all` prefix so one invalidation covers both. */
  stats: (params: BuyerSubmissionListParams) =>
    [...buyerSubmissionKeys.all, "stats", params] as const,

  /** Has this buyer already been submitted to this listing? */
  duplicateCheck: (buyerLeadId: string, listingId: string) =>
    [...buyerSubmissionKeys.all, "duplicate-check", buyerLeadId, listingId] as const,

  /** The standing presentations for a buyer or a listing (ruling R1) — the
   *  present pickers' "Already presented" source. Under `all`, so every
   *  submission write (present, archive, delete) refreshes it. */
  presentations: (params: { buyerLeadId?: string; listingId?: string }) =>
    [...buyerSubmissionKeys.all, "presentations", params.buyerLeadId ?? "", params.listingId ?? ""] as const,

  /** Package state (PR 2c). Under the `all` prefix, so generating or sending
   *  refreshes the submission list's status column with one invalidation. */
  package: (id: string) => [...buyerSubmissionKeys.all, "package", id] as const,

  /** A MUTATION key, not a query: Generate all, each card's Generate and the
   *  document editor's save share it, so the panel runs one document write per
   *  submission at a time. Overlapping writes made the slower one (the cover
   *  letter's AI call) fail on save, and a cover letter written while a sibling
   *  was rewritten would describe the package as it was. */
  packageDocumentWrite: (id: string) =>
    [...buyerSubmissionKeys.all, "package-document-write", id] as const,
  packageDownload: (id: string) =>
    [...buyerSubmissionKeys.all, "package-download", id] as const,

  /** The buyer-facing cover email for the same package. A plain query, unlike
   *  the intro email's download companion: this endpoint stamps nothing, so
   *  caching it cannot record a contact that did not happen. */
  packageBuyerDraft: (id: string) =>
    [...buyerSubmissionKeys.all, "package-buyer-draft", id] as const,

  /**
   * The buyer-facing listing intro email (WS-10).
   *
   * ONE key for the whole surface, and deliberately no twin for the download
   * companion the package has: that endpoint STAMPS THE SEND, so it is a
   * mutation here rather than a query. A cached read of it would re-record a
   * contact on any refetch.
   *
   * Under the `all` prefix so a send refreshes the submission detail alongside
   * it — the send moves nothing on the list's status column (status is
   * deliberately untouched by this feature), but the detail read is the same
   * record.
   */
  introEmail: (id: string) => [...buyerSubmissionKeys.all, "intro-email", id] as const,

  /**
   * The broker-facing intro email — the step BEFORE the package.
   *
   * <p>ONE key for the whole surface, and deliberately no twin for the download
   * companion, for the same reason its sibling has none: that endpoint RECORDS
   * THE SEND, so it is a mutation here rather than a query. A cached read of it
   * would re-record a send on any refetch.</p>
   *
   * <p>Under the `all` prefix, so drafting or sending refreshes the submission
   * detail beside it. 🔴 It deliberately does NOT reach `buyerKeys` — the
   * intro email's send does, because that one stamps the BUYER's contact
   * clocks, and this one must never.</p>
   */
  brokerIntro: (id: string) => [...buyerSubmissionKeys.all, "broker-intro", id] as const,

  /** COPY FOR MY OWN EMAIL's text — a plain read since R4 (it records nothing),
   *  under `all` so a redraft or an edit refreshes it. */
  brokerIntroDownload: (id: string) =>
    [...buyerSubmissionKeys.all, "broker-intro-download", id] as const,

  /** The Broker intro tab's readiness strip and its text / web form /
   *  follow-up cards (D24, D25). Under `all`, so every send, log or agent edit
   *  refreshes it with the submission. */
  brokerOutreach: (id: string) => [...buyerSubmissionKeys.all, "broker-outreach", id] as const,

  /** Documents filed in one submission's folder (PR 3b) — the NDA attestation
   *  picker's source. Under the `all` prefix so recording an NDA refreshes it. */
  documents: (id: string) => [...buyerSubmissionKeys.all, "documents", id] as const,

  /**
   * A versioned paperwork record — its whole version stack and every entry
   * (WS4/WS7).
   *
   * <p>Under the `all` prefix so recording an NDA fact refreshes the record, the
   * detail read whose mirror columns follow it, and the list's settled flag in
   * ONE invalidation. Keyed by kind as well as id, because a submission can hold
   * more than one record and appending to the NDA must not evict a sibling.</p>
   */
  record: (id: string, kind: BuyerRecordKind) =>
    [...buyerSubmissionKeys.all, "record", id, kind] as const,

  /**
   * The conversation log, PAGED.
   *
   * <p>The log used to ride the detail payload as an unbounded array — every
   * line ever recorded, fetched on every read of the submission, whether or not
   * anybody scrolled to it. It becomes a tab of its own with its own read.</p>
   *
   * <p>Under the `all` prefix so logging a line still refreshes the detail and
   * the list's conversation count in ONE invalidation. The page number is part
   * of the key rather than a filter object, because paging is the only axis and
   * a bare number keeps `all`-prefix matching intact.</p>
   */

  /** The escalation gate (Escalation PR-2). Under the `all` prefix, so
   *  escalating refreshes the list's status column and the detail in one
   *  invalidation. */
  escalation: (id: string) => [...buyerSubmissionKeys.all, "escalation", id] as const,

  /**
   * The Potential Deal Structure — inputs, computed figures and the Suggested
   * structure.
   *
   * <p>Under the `all` prefix so that one invalidation after a save refreshes
   * it together with what it moves: the detail read (step, completion, price),
   * the package's blocked reason for document 3, and the lists' "Deal
   * structure price" column.</p>
   */
  dealStructure: (id: string) => [...buyerSubmissionKeys.all, "deal-structure", id] as const,

  /** The LISTING's seller prequalification, read from a submission (Escalation
   *  PR-4). Keyed by SUBMISSION because that is what the endpoint is addressed
   *  by — and under the `all` prefix so linking or unlinking also refreshes the
   *  escalation gate, whose warning reads the same signal. */
  sellerPrequalification: (id: string) =>
    [...buyerSubmissionKeys.all, "seller-prequalification", id] as const,
  sellerPrequalificationCandidates: (id: string, search: string) =>
    [...buyerSubmissionKeys.all, "seller-prequalification-candidates", id, search] as const,
  sellerPrequalificationInvites: (id: string) =>
    [...buyerSubmissionKeys.all, "seller-prequalification-invites", id] as const,
};

export const buyerAssignmentKeys = {
  all: ["buyer-assignments"] as const,
  queue: (params: BuyerAssignmentQueueParams) =>
    [...buyerAssignmentKeys.all, "queue", params] as const,
  grants: () => [...buyerAssignmentKeys.all, "grants"] as const,
  /** Who the caller may hand a buyer to. Keyed by buyer (`""` = a buyer about
   *  to be created) and under the `all` prefix, so an assign, an unassign or a
   *  roster move refreshes it with the queue. */
  candidates: (buyerLeadId: string | null | undefined) =>
    [...buyerAssignmentKeys.all, "candidates", buyerLeadId ?? ""] as const,
  /** Who the caller may hand an existing buyer to. ONE key for the caller, so
   *  every row on a page shares one request; under `all` like `candidates`. */
  handOffRoster: () => [...buyerAssignmentKeys.all, "hand-off-roster"] as const,
};

/**
 * The allocation policy and the entitlement it produces (PR 2b).
 *
 * Entitlement is COMPUTED per request from the qualifying stamps — there is no
 * counter column — so anything that moves a buyer's owner or sends a package
 * changes it. That makes `all` the right blast radius for a policy write, an
 * assignment, and an Auto-Distribute run alike: `settings` and `entitlements`
 * both sit under it, and `entitlements()` is itself a prefix of the caller's own
 * standing, so one invalidation reaches the manager's table and the specialist's
 * widget together.
 */
export const buyerAllocationKeys = {
  all: ["buyer-allocation"] as const,
  settings: () => [...buyerAllocationKeys.all, "settings"] as const,
  /** All standings the caller dispatches, or one team's (the team ALLOCATION
   *  tab). The team-scoped key sits UNDER the unscoped one, so the single
   *  `entitlements()` prefix an assignment invalidates still reaches it. */
  entitlements: (buyerTeamId?: string) =>
    buyerTeamId
      ? ([...buyerAllocationKeys.all, "entitlements", "team", buyerTeamId] as const)
      : ([...buyerAllocationKeys.all, "entitlements"] as const),
  myEntitlement: () => [...buyerAllocationKeys.entitlements(), "me"] as const,
};

/**
 * Buyer teams (WS4) — the registry, one team's detail, and the roster.
 *
 * `all` is the blast radius for any membership move: a move changes the roster
 * on TWO teams and the member counts on the list, so a narrower invalidation
 * would leave the team somebody was moved OFF showing them for the five minutes
 * of `staleTime`, with no recovery.
 */
export const buyerTeamKeys = {
  all: ["buyer-teams"] as const,
  list: (includeArchived: boolean) =>
    [...buyerTeamKeys.all, "list", includeArchived] as const,
  detail: (id: string) => [...buyerTeamKeys.all, "detail", id] as const,
};

/**
 * Advertising (WS6) — budgets, advertisements, the spend ledger and the ROI
 * roll-up.
 *
 * Every read is derived from the spend ledger and the leads attributed to an
 * advertisement, so recording spend moves the ad row, the team card AND the
 * roll-up. `all` is therefore the right blast radius for any write here, the
 * same reasoning `buyerAllocationKeys.all` carries.
 */
export const advertisingKeys = {
  all: ["buyer-advertising"] as const,
  budgets: (teamId: string) => [...advertisingKeys.all, "budgets", teamId] as const,
  ads: (teamId: string) => [...advertisingKeys.all, "ads", teamId] as const,
  ad: (id: string) => [...advertisingKeys.all, "ad", id] as const,
  spend: (adId: string) => [...advertisingKeys.all, "spend", adId] as const,
  rollup: (period: string) => [...advertisingKeys.all, "rollup", period] as const,
  /** Every advertisement the caller may attribute a lead to, across the teams
   *  they read — the intake pickers' source. Under `all`, so creating or
   *  archiving an advertisement refreshes it. */
  attributable: () => [...advertisingKeys.all, "attributable"] as const,
};

/** The Broker intro tab's fallback wording (R9) — admin-only, one list. */
export const brokerOutreachWordingKeys = {
  all: ["broker-outreach-wording"] as const,
};
